import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import AvatarPicker from "../components/app/AvatarPicker";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import MagicLinkForm from "../components/app/MagicLinkForm";
import { captureSignupSource, getSignupSource, clearSignupSource } from "../lib/signupSource";
import { useSeo } from "../lib/useSeo";

// Only allow internal paths in the post-signup redirect — prevents open
// redirects to attacker-controlled domains.
function safeNext(next) {
  if (!next || typeof next !== "string") return "/browse";
  if (!next.startsWith("/") || next.startsWith("//")) return "/browse";
  return next;
}

export default function Register() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [picture, setPicture] = useState(null);

  // iter222 — tell Google to crawl but not index this utility page so
  // the "Indexed, though blocked by robots.txt" warning clears.
  useSeo({ title: "Sign up free — CampCompass UK", noindex: true });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t("auth.passwordTooShort")); return; }
    setBusy(true);
    const attribution = getSignupSource();
    const res = await register(email, password, name, birthDate || null, picture || null, attribution);
    setBusy(false);
    if (res.ok) {
      clearSignupSource();
      toast.success(t("auth.accountCreated"));
      // iter195 — paid tiers paused. New signups now get the celebratory
      // WelcomeUnlockedModal on the landing page (or the ?next= target)
      // instead of being funnelled into Stripe Checkout. `?welcome=1` on
      // the destination tells `WelcomeUnlockedModal` to fire once, then
      // POSTs `/auth/mark-welcome-seen` so it never repeats.
      const dest = next && next !== "/browse" ? next : "/";
      const sep = dest.includes("?") ? "&" : "?";
      nav(`${dest}${sep}welcome=1`);
    }
    else toast.error(res.error);
  };

  // Capture ?ref= / UTM / referrer on first mount so we can attribute the
  // signup to the right marketing channel even if the user pokes around
  // before submitting the form.
  useEffect(() => { captureSignupSource(); }, []);

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="register-page">
      <Header />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="eyebrow mb-3">{t("auth.newMembersEyebrow")}</div>
        <h1 className="font-display text-4xl tracking-tight mb-3">{t("auth.createAccountTitle")}</h1>

        {/* iter122 — Tiny community-values line above the form. Mountain
            Rescue is the highest-profile charity we publicly back; calling
            it out at the moment of sign-up frames CampCompass as the kind of
            community a new member wants to join, not just a utility app. */}
        <p className="text-sm text-[#555] leading-relaxed mb-6" data-testid="register-values-line">
          Join 22,000+ camp-sites and a community that quietly champions{" "}
          <Link to="/charities" className="text-[#16a34a] underline underline-offset-2 hover:text-[#0e0e0e]">
            Mountain Rescue
          </Link>
          {" "}— the volunteers who keep the wild places safe.
        </p>

        {/* iter177 — Google auth moved ABOVE the form. Presence dashboard
            showed most first-time visitors bounce from the wall of form
            fields; a single one-tap Google button collapses that ask to a
            single click. */}
        <button
          onClick={onGoogle}
          className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-[#0e0e0e] hover:bg-[#fafafa] text-[#0e0e0e] font-bold text-sm px-4 py-3 shadow-[0_4px_0_0_#0e0e0e] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#0e0e0e] transition-transform mb-4"
          data-testid="google-register"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google — 1 tap
        </button>

        <div className="mb-4 flex items-center gap-3 text-xs text-[#888]">
          <span className="flex-1 h-px bg-[#ddd]" /> {t("auth.or")} · magic link <span className="flex-1 h-px bg-[#ddd]" />
        </div>

        {/* iter206 — one-click magic-link sign-up: same form works for new
            emails (creates the account) or existing ones (signs in). */}
        <MagicLinkForm testId="register-magic" />

        <div className="mb-4 mt-6 flex items-center gap-3 text-xs text-[#888]">
          <span className="flex-1 h-px bg-[#ddd]" /> or set a password <span className="flex-1 h-px bg-[#ddd]" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4 p-6 border hairline bg-white" data-testid="register-form">
          {/* iter199 — signup form pared to the essentials (email + password
              + name). Birthday, avatar, and any other optional fields moved
              to the post-signup profile flow. Faster form = higher conversion. */}
          <div>
            <label className="eyebrow block mb-2">{t("auth.name")}</label>
            <input className="cc-input" value={name} onChange={(e) => setName(e.target.value)}
              required data-testid="register-name" placeholder="First name is fine" />
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("auth.email")}</label>
            <input type="email" className="cc-input" value={email} onChange={(e) => setEmail(e.target.value)}
              required data-testid="register-email" />
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("auth.password")}</label>
            <input type="password" className="cc-input" value={password} onChange={(e) => setPassword(e.target.value)}
              required minLength={6} data-testid="register-password" placeholder="At least 6 characters" />
          </div>
          <button className="btn-primary w-full flex items-center justify-center gap-2" disabled={busy} data-testid="register-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.signUpBtn")}
          </button>
          <p className="text-[10px] text-[#888] text-center mt-2">
            Takes under 30 seconds · no card required · we never spam
          </p>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-[#888]">
          <span className="flex-1 h-px bg-[#ddd]" /> already a member? <span className="flex-1 h-px bg-[#ddd]" />
        </div>

        <div className="mt-2 text-sm text-[#555] text-center">
          {t("auth.haveAccount")} <Link to={`/login${next !== "/browse" ? `?next=${encodeURIComponent(next)}` : ""}`} className="underline text-[#ff4500]" data-testid="to-login">{t("auth.logIn")}</Link>
        </div>
      </div>
    </div>
  );
}
